import { useState, useMemo } from 'react';
import { StatusBadge, Button, Modal, Input, Textarea } from '../components/common';
import { calculateTotalEarnings, calculatePendingEarnings } from '../services/commissionService';

/**
 * Admin Financial Page
 * Financial overview and payout management for admins
 */
export function AdminFinancialPage({ 
  allCommissions, 
  loadingCommissions, 
  markCommissionPaid, 
  bulkMarkCommissionsPaid 
}) {
  const [selectedIds, setSelectedIds] = useState([]);
  const [showPayoutModal, setShowPayoutModal] = useState(false);
  const [payoutNote, setPayoutNote] = useState('');

  // Calculate totals
  const totalPaid = useMemo(() => 
    calculateTotalEarnings(allCommissions),
    [allCommissions]
  );

  const totalPending = useMemo(() => 
    calculatePendingEarnings(allCommissions),
    [allCommissions]
  );

  // Group by user
  const userStats = useMemo(() => {
    const stats = {};
    allCommissions.forEach(comm => {
      if (!stats[comm.user_id]) {
        stats[comm.user_id] = {
          user_id: comm.user_id,
          user_role: comm.user_role,
          total: 0,
          paid: 0,
          pending: 0,
          count: 0,
        };
      }
      stats[comm.user_id].count++;
      stats[comm.user_id].total += parseFloat(comm.amount);
      if (comm.status === 'paid') {
        stats[comm.user_id].paid += parseFloat(comm.amount);
      } else {
        stats[comm.user_id].pending += parseFloat(comm.amount);
      }
    });
    return Object.values(stats);
  }, [allCommissions]);

  const handleBulkPayout = async () => {
    if (selectedIds.length === 0) return;
    
    await bulkMarkCommissionsPaid(selectedIds, payoutNote);
    setSelectedIds([]);
    setPayoutNote('');
    setShowPayoutModal(false);
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) 
        ? prev.filter(i => i !== id)
        : [...prev, id]
    );
  };

  const selectAll = () => {
    const pendingIds = allCommissions
      .filter(c => c.status === 'pending')
      .map(c => c.id);
    setSelectedIds(pendingIds);
  };

  if (loadingCommissions) {
    return (
      <div style={{ padding: 32, textAlign: "center", color: "#000000" }}>
        جاري تحميل التقارير المالية...
      </div>
    );
  }

  return (
    <div style={{ padding: "clamp(16px, 3vw, 32px)" }}>
      {/* Header */}
      <div style={{ marginBottom: 32 }}>
        <h1 style={{ color: "#000000", fontSize: 28, fontWeight: 900, marginBottom: 8 }}>
          📊 التقارير المالية
        </h1>
        <p style={{ color: "#000000", fontSize: 14 }}>
          إدارة العمولات والمدفوعات
        </p>
      </div>

      {/* Summary Cards */}
      <div style={{ 
        display: "grid", 
        gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", 
        gap: 16, 
        marginBottom: 32 
      }}>
        <div style={{
          background: "linear-gradient(135deg, #ffffff, #ffffff)",
          border: "1px solid #10b981",
          borderRadius: 16,
          padding: 24,
        }}>
          <p style={{ color: "#6ee7b7", fontSize: 12, marginBottom: 8 }}>إجمالي المدفوعات</p>
          <p style={{ color: "#34d399", fontSize: 32, fontWeight: 900 }}>
            {totalPaid.toFixed(2)} AED
          </p>
        </div>

        <div style={{
          background: "linear-gradient(135deg, #ffffff, #ffffff)",
          border: "1px solid #f59e0b",
          borderRadius: 16,
          padding: 24,
        }}>
          <p style={{ color: "#fcd34d", fontSize: 12, marginBottom: 8 }}>المعلقة</p>
          <p style={{ color: "#f59e0b", fontSize: 32, fontWeight: 900 }}>
            {totalPending.toFixed(2)} AED
          </p>
        </div>

        <div style={{
          background: "linear-gradient(135deg, #ffffff, #ffffff)",
          border: "1px solid #4ecdc4",
          borderRadius: 16,
          padding: 24,
        }}>
          <p style={{ color: "#93c5fd", fontSize: 12, marginBottom: 8 }}>إجمالي العمولات</p>
          <p style={{ color: "#4ecdc4", fontSize: 32, fontWeight: 900 }}>
            {allCommissions.length}
          </p>
        </div>

        <div style={{
          background: "linear-gradient(135deg, #200d50, #2a1170)",
          border: "1px solid #a78bfa",
          borderRadius: 16,
          padding: 24,
        }}>
          <p style={{ color: "#c4b5fd", fontSize: 12, marginBottom: 8 }}>المستخدمين</p>
          <p style={{ color: "#a78bfa", fontSize: 32, fontWeight: 900 }}>
            {userStats.length}
          </p>
        </div>
      </div>

      {/* Bulk Actions */}
      {selectedIds.length > 0 && (
        <div style={{
          background: "#ffffff",
          border: "1px solid #4ecdc4",
          borderRadius: 12,
          padding: 16,
          marginBottom: 24,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center"
        }}>
          <span style={{ color: "#000000", fontWeight: 700 }}>
            ✅ تم اختيار {selectedIds.length} عمولة
          </span>
          <div style={{ display: "flex", gap: 12 }}>
            <Button onClick={() => setShowPayoutModal(true)}>
              تحديد كمدفوعة
            </Button>
            <Button variant="secondary" onClick={() => setSelectedIds([])}>
              إلغاء التحديد
            </Button>
          </div>
        </div>
      )}

      {/* User Stats */}
      <div style={{
        background: "#ffffff",
        borderRadius: 16,
        border: "1px solid #e2e8f0",
        marginBottom: 24,
        overflow: "hidden"
      }}>
        <div style={{ padding: 20, borderBottom: "1px solid #e2e8f0" }}>
          <h2 style={{ color: "#000000", fontSize: 18, fontWeight: 700, margin: 0 }}>
            إحصائيات المستخدمين
          </h2>
        </div>
        
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#ffffff" }}>
                <th style={{ padding: "12px 16px", color: "#3d5470", fontSize: 11, fontWeight: 700, textAlign: "right" }}>
                  <input 
                    type="checkbox"
                    onChange={selectAll}
                    style={{ width: 16, height: 16, cursor: "pointer" }}
                  />
                </th>
                <th style={{ padding: "12px 16px", color: "#3d5470", fontSize: 11, fontWeight: 700, textAlign: "right" }}>
                  عدد العمولات
                </th>
                <th style={{ padding: "12px 16px", color: "#3d5470", fontSize: 11, fontWeight: 700, textAlign: "right" }}>
                  المدفوعة
                </th>
                <th style={{ padding: "12px 16px", color: "#3d5470", fontSize: 11, fontWeight: 700, textAlign: "right" }}>
                  المعلقة
                </th>
                <th style={{ padding: "12px 16px", color: "#3d5470", fontSize: 11, fontWeight: 700, textAlign: "right" }}>
                  الإجمالي
                </th>
              </tr>
            </thead>
            <tbody>
              {userStats.map(stat => (
                <tr key={stat.user_id} style={{ borderTop: "1px solid #e2e8f0" }}>
                  <td style={{ padding: "12px 16px" }}>
                    <input 
                      type="checkbox"
                      checked={selectedIds.some(id => allCommissions.find(c => c.id === id)?.user_id === stat.user_id)}
                      onChange={() => {
                        const userCommissionIds = allCommissions
                          .filter(c => c.user_id === stat.user_id && c.status === 'pending')
                          .map(c => c.id);
                        
                        if (selectedIds.some(id => userCommissionIds.includes(id))) {
                          setSelectedIds(prev => prev.filter(id => !userCommissionIds.includes(id)));
                        } else {
                          setSelectedIds(prev => [...prev, ...userCommissionIds]);
                        }
                      }}
                      style={{ width: 16, height: 16, cursor: "pointer" }}
                    />
                  </td>
                  <td style={{ padding: "12px 16px", color: "#000000", fontWeight: 700 }}>
                    {stat.count}
                  </td>
                  <td style={{ padding: "12px 16px", color: "#10b981", fontWeight: 700 }}>
                    {stat.paid.toFixed(2)} AED
                  </td>
                  <td style={{ padding: "12px 16px", color: "#f59e0b", fontWeight: 700 }}>
                    {stat.pending.toFixed(2)} AED
                  </td>
                  <td style={{ padding: "12px 16px", color: "#4ecdc4", fontWeight: 700 }}>
                    {stat.total.toFixed(2)} AED
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* All Commissions Table */}
      <div style={{
        background: "#ffffff",
        borderRadius: 16,
        border: "1px solid #e2e8f0",
        overflow: "hidden"
      }}>
        <div style={{ padding: 20, borderBottom: "1px solid #e2e8f0" }}>
          <h2 style={{ color: "#000000", fontSize: 18, fontWeight: 700, margin: 0 }}>
            جميع العمولات
          </h2>
        </div>

        {allCommissions.length === 0 ? (
          <div style={{ padding: 48, textAlign: "center", color: "#64748b" }}>
            <p style={{ fontSize: 48, marginBottom: 16 }}>📊</p>
            <p style={{ fontSize: 16, fontWeight: 700 }}>لا توجد عمولات</p>
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "#ffffff" }}>
                  <th style={{ padding: "12px 16px", color: "#3d5470", fontSize: 11, fontWeight: 700, textAlign: "right" }}>
                    المطعم
                  </th>
                  <th style={{ padding: "12px 16px", color: "#3d5470", fontSize: 11, fontWeight: 700, textAlign: "right" }}>
                    المستخدم
                  </th>
                  <th style={{ padding: "12px 16px", color: "#3d5470", fontSize: 11, fontWeight: 700, textAlign: "right" }}>
                    المبلغ
                  </th>
                  <th style={{ padding: "12px 16px", color: "#3d5470", fontSize: 11, fontWeight: 700, textAlign: "right" }}>
                    الحالة
                  </th>
                  <th style={{ padding: "12px 16px", color: "#3d5470", fontSize: 11, fontWeight: 700, textAlign: "right" }}>
                    التاريخ
                  </th>
                </tr>
              </thead>
              <tbody>
                {allCommissions.map(comm => (
                  <tr key={comm.id} style={{ borderTop: "1px solid #e2e8f0" }}>
                    <td style={{ padding: "12px 16px", color: "#000000" }}>
                      <div style={{ fontWeight: 700 }}>{comm.restaurant?.name || 'N/A'}</div>
                      <div style={{ fontSize: 11, color: "#64748b" }}>{comm.restaurant?.area || ''}</div>
                    </td>
                    <td style={{ padding: "12px 16px", color: "#000000", fontSize: 13 }}>
                      {comm.user_role === 'marketer' ? 'منسّقة' : 'مندوب'}
                    </td>
                    <td style={{ padding: "12px 16px", color: "#10b981", fontWeight: 700 }}>
                      {parseFloat(comm.amount).toFixed(2)} AED
                    </td>
                    <td style={{ padding: "12px 16px" }}>
                      <StatusBadge 
                        status={comm.status === 'paid' ? 'deal_closed' : 'pending_closure'} 
                      />
                    </td>
                    <td style={{ padding: "12px 16px", color: "#000000", fontSize: 13 }}>
                      {new Date(comm.created_at).toLocaleDateString('ar-AE')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Payout Modal */}
      <Modal 
        title="✅ تحديد كمدفوعة" 
        isOpen={showPayoutModal} 
        onClose={() => setShowPayoutModal(false)}
        size="md"
      >
        <Textarea
          label="ملاحظات الدفع (اختياري)"
          value={payoutNote}
          onChange={e => setPayoutNote(e.target.value)}
          placeholder="رقم التحويل، المرجع البنكي..."
          rows={4}
        />
        
        <div style={{ display: "flex", gap: 12, marginTop: 24 }}>
          <Button 
            onClick={handleBulkPayout}
            style={{ flex: 1, justifyContent: "center" }}
          >
            تأكيد الدفع ({selectedIds.length} عمولة)
          </Button>
          <Button 
            variant="secondary" 
            onClick={() => setShowPayoutModal(false)}
            style={{ flex: 1, justifyContent: "center" }}
          >
            إلغاء
          </Button>
        </div>
      </Modal>
    </div>
  );
}
